<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <form @submit.prevent="saveCompany">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-1/2 mx-auto flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <h1 class="stage-page-title">Unternehmen bearbeiten</h1>
                    <div class="inline-flex">
                        <router-link class="btn-stage-secondary mr-3" :to="companyListPath">
                            {{ $t('Back') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            Unternehmen speichern
                        </button>
                    </div>
                </div>
            </div>
            <div class="stage-page-body">
                <div class="w-full xl:w-1/2 mx-auto space-y-6">
                    <loading :status="loading.form"/>
                    <div class="space-y-6">
                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Unternehmen
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-6">
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="company-type">Typ</label>
                                    <select
                                        id="company-type"
                                        v-model="company.company_type"
                                        class="stage-filter-input form-select block w-full text-sm sm:leading-5"
                                        required
                                    >
                                        <option value="company">Unternehmen</option>
                                        <option value="institution">Institution</option>
                                        <option value="association">Verein</option>
                                        <option value="law_firm">Kanzlei</option>
                                    </select>
                                </div>
                                <div class="md:col-span-4">
                                    <label class="stage-filter-label block mb-2" for="company-name">Name</label>
                                    <input
                                        id="company-name"
                                        v-model="company.name"
                                        class="stage-filter-input form-input block w-full text-sm sm:leading-5"
                                        maxlength="255"
                                        placeholder="Unternehmensname"
                                        required
                                    >
                                </div>
                                <div class="md:col-span-6">
                                    <label class="stage-filter-label block mb-2">Unternehmensbild</label>
                                    <div class="flex items-center select-none">
                                        <div class="avatar-editor relative inline-block h-12 w-12 rounded-sm overflow-hidden bg-gray-100">
                                            <button
                                                v-show="hasCustomImage"
                                                class="avatar-editor-remove absolute top-0 right-0 items-center p-0.5 rounded-full leading-4 bg-red-600 text-white cursor-pointer"
                                                type="button"
                                                @click="removeImage"
                                            >
                                                <svg-vue class="h-4 w-4 p-px" icon="font-awesome.times-light"></svg-vue>
                                            </button>
                                            <img
                                                :src="imagePreviewUrl"
                                                :alt="company.name || 'Unternehmensbild'"
                                                class="h-full w-full text-gray-300 rounded-sm object-cover"
                                            />
                                        </div>
                                        <span class="ml-5">
                                            <input ref="changeImage" accept=".png,.jpg,.jpeg" hidden type="file" @change="changeImage($event)">
                                            <button
                                                class="py-2 px-3 border border-bordergray-100 rounded text-sm leading-4 font-medium text-gray-700 bg-white hover:text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-0"
                                                type="button"
                                                @click="selectImage"
                                            >
                                                Ändern
                                            </button>
                                        </span>
                                    </div>
                                    <p class="mt-2 text-xs leading-4 text-gray-500">
                                        Quadratisches PNG oder JPEG, maximal 1000 × 1000 Pixel.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Anschrift
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-6">
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="company-street">Straße</label>
                                    <input id="company-street" v-model.trim="company.street" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" required>
                                </div>
                                <div class="md:col-span-1">
                                    <label class="stage-filter-label block mb-2" for="company-house-number">Hausnummer</label>
                                    <input id="company-house-number" v-model.trim="company.house_number" placeholder="Nr" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="32" required>
                                </div>
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="company-postal-code">PLZ</label>
                                    <input id="company-postal-code" v-model.trim="company.postal_code" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="20" pattern="[A-Za-z0-9][A-Za-z0-9 \-]{1,19}" required title="Bitte geben Sie eine gültige Postleitzahl ein.">
                                </div>
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="company-city">Ort</label>
                                    <input id="company-city" v-model.trim="company.city" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="120" required>
                                </div>
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="company-country">Land</label>
                                    <input id="company-country" v-model.trim="company.country" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="120" required>
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Kontakt
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-email">E-Mail (Zentrale)</label>
                                    <input id="company-email" v-model.trim="company.email" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" required type="email">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-phone">Telefon (Zentrale)</label>
                                    <input id="company-phone" v-model.trim="company.phone" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="64" pattern="\+?[0-9][0-9 ()/.\-]{2,63}" required title="Bitte geben Sie eine gültige Telefonnummer ein." type="tel">
                                </div>
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="company-website">Website</label>
                                    <div class="relative flex rounded">
                                        <span class="inline-flex items-center px-3 rounded-l border border-r-0 border-bordergray-100 bg-gray-50 text-gray-500 text-sm">https://</span>
                                        <input
                                            id="company-website"
                                            v-model="company.website"
                                            class="stage-filter-input form-input block w-full rounded-l-none text-sm sm:leading-5"
                                            inputmode="url"
                                            maxlength="255"
                                            placeholder="example.com"
                                        >
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Social Media
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-linkedin">LinkedIn</label>
                                    <input id="company-linkedin" v-model.trim="company.linkedin_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.linkedin.com/company/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-xing">XING</label>
                                    <input id="company-xing" v-model.trim="company.xing_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.xing.com/pages/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-instagram">Instagram</label>
                                    <input id="company-instagram" v-model.trim="company.instagram_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.instagram.com/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-facebook">Facebook</label>
                                    <input id="company-facebook" v-model.trim="company.facebook_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.facebook.com/..." type="url">
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Steuerdaten
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-tax-number">Steuernummer</label>
                                    <input id="company-tax-number" v-model="company.tax_number" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="120" pattern="[A-Za-z0-9][A-Za-z0-9 ./\-]*" title="Bitte geben Sie eine gültige Steuernummer ein.">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="company-vat-id">USt-ID</label>
                                    <input id="company-vat-id" v-model="company.vat_id" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="23" pattern="[A-Za-z]{2}[A-Za-z0-9 .\-]{2,20}" placeholder="DE123456789" title="Bitte geben Sie eine gültige USt-ID ein.">
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
                <div class="w-full xl:w-1/2 mx-auto bg-white shadow sm:rounded overflow-hidden mt-6">
                    <loading :status="loading.users"/>
                    <h3 class="flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                        {{ company.is_contact_company ? 'Unternehmensrolle und Sichtbarkeit' : 'Abrechnung und Berechtigungen' }}
                    </h3>
                    <div class="p-6">
                        <div class="space-y-6">
                            <div>
                                <label class="block text-sm font-medium leading-5 text-gray-700" for="company-role-display">
                                    Unternehmensrolle
                                </label>
                                <div class="mt-1 relative rounded shadow-sm">
                                    <input
                                        id="company-role-display"
                                        :value="companyRoleLabel"
                                        class="form-input block w-full cursor-default bg-gray-50 text-gray-700 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        readonly
                                        type="text"
                                    >
                                </div>
                            </div>
                            <div v-if="company.is_contact_company" class="rounded border border-bordergray-100 p-4">
                                <h4 class="text-sm font-semibold text-gray-900">Sichtbarkeit im Bereich Beziehungen</h4>
                                <p class="mt-1 text-sm text-gray-500">
                                    Mitarbeiter und Mitarbeitende von Dienstleistern sehen dieses Unternehmen mit der Rolle „Externer Kontakt“ nur nach ausdrücklicher Freigabe.
                                </p>
                                <div class="mt-4 space-y-3">
                                    <label class="flex items-center text-sm text-gray-700">
                                        <input v-model="company.relationships_visible_to_employees" class="form-checkbox h-4 w-4 text-gray-700 mr-3" type="checkbox">
                                        Mitarbeiter
                                    </label>
                                    <label class="flex items-center text-sm text-gray-700">
                                        <input v-model="company.relationships_visible_to_service_providers" class="form-checkbox h-4 w-4 text-gray-700 mr-3" type="checkbox">
                                        Mitarbeitende Dienstleister
                                    </label>
                                </div>
                                <h5 class="mt-5 text-sm font-semibold text-gray-900">Für Kundenunternehmen sichtbar</h5>
                                <p class="mt-1 text-sm text-gray-500">
                                    Kunden sehen dieses Unternehmen mit der Rolle „Externer Kontakt“ ausschließlich, wenn ihr eigenes Unternehmen hier ausdrücklich ausgewählt wurde.
                                </p>
                                <div class="form-selection-panel mt-3 max-h-56 overflow-y-auto">
                                    <label
                                        v-for="customerCompany in customerCompanyOptions"
                                        :key="'contact-company-customer-' + customerCompany.id"
                                        class="form-selection-option flex cursor-pointer items-center px-3 py-2"
                                    >
                                        <input
                                            v-model="company.relationship_customer_company_ids"
                                            :value="Number(customerCompany.id)"
                                            class="form-checkbox mr-3 h-4 w-4 flex-shrink-0 text-gray-700"
                                            type="checkbox"
                                        >
                                        <img
                                            :alt="customerCompany.name"
                                            :src="customerCompany.image"
                                            class="mr-3 h-6 w-6 flex-shrink-0 rounded-sm object-cover"
                                        >
                                        <span class="truncate text-sm text-gray-700">{{ customerCompany.name }}</span>
                                    </label>
                                    <div v-if="customerCompanyOptions.length < 1" class="px-3 py-2 text-sm text-gray-500">
                                        Keine Kundenunternehmen verfügbar.
                                    </div>
                                </div>
                            </div>
                            <template v-if="!company.is_operator_company && !company.is_contact_company">
                                <div class="space-y-4">
                                    <div class="space-y-6 rounded border border-bordergray-100 p-4">
                                        <div v-if="company.is_customer_company" class="space-y-4">
                                            <h5 class="text-sm font-semibold text-gray-900">Abrechnung (Betreiber an Kunde)</h5>
                                            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                                                <div>
                                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="company-customer-hourly-rate">Stundensatz (Netto) (€)</label>
                                                    <div class="mt-1 relative rounded shadow-sm">
                                                        <input
                                                            id="company-customer-hourly-rate"
                                                            v-model="company.customer_hourly_rate"
                                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                            min="0"
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            type="number"
                                                        >
                                                    </div>
                                                </div>
                                                <div>
                                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="company-customer-vat-rate">Umsatzsteuer (%)</label>
                                                    <div class="mt-1 relative rounded shadow-sm">
                                                        <input
                                                            id="company-customer-vat-rate"
                                                            v-model="company.customer_vat_rate"
                                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                            max="100"
                                                            min="0"
                                                            placeholder="19"
                                                            step="0.01"
                                                            type="number"
                                                        >
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="company.is_provider_company" class="space-y-4">
                                            <h5 class="text-sm font-semibold text-gray-900">Abrechnung (Dienstleister an Betreiber)</h5>
                                            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                                                <div>
                                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="company-provider-hourly-rate">Stundensatz (Netto) (€)</label>
                                                    <div class="mt-1 relative rounded shadow-sm">
                                                        <input
                                                            id="company-provider-hourly-rate"
                                                            v-model="company.provider_hourly_rate"
                                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                            min="0"
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            type="number"
                                                        >
                                                    </div>
                                                </div>
                                                <div>
                                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="company-provider-vat-rate">Umsatzsteuer (%)</label>
                                                    <div class="mt-1 relative rounded shadow-sm">
                                                        <input
                                                            id="company-provider-vat-rate"
                                                            v-model="company.provider_vat_rate"
                                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                            max="100"
                                                            min="0"
                                                            placeholder="19"
                                                            step="0.01"
                                                            type="number"
                                                        >
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <div v-if="company.is_contact_company" class="rounded border border-bordergray-100 overflow-hidden">
                                <div class="border-b border-bordergray-100 bg-gray-50 px-4 py-3">
                                    <h4 class="text-sm font-semibold text-gray-900">Zugeordnete Accounts</h4>
                                    <p class="mt-1 text-sm text-gray-500">
                                        Diese Zuordnung dient ausschließlich der Gruppierung im Bereich Beziehungen. Sie gewährt keine Zugriffs-, Projekt-, Ticket-, Positions- oder Finanzrechte.
                                    </p>
                                </div>
                                <div class="max-h-[28rem] divide-y divide-bordergray-100 overflow-y-auto bg-white">
                                    <label
                                        v-for="user in availableUsers"
                                        :key="'contact-account-' + user.id"
                                        class="flex cursor-pointer items-center px-4 py-3"
                                    >
                                        <input
                                            v-model="company.contact_user_ids"
                                            :value="Number(user.id)"
                                            class="form-checkbox mr-3 h-4 w-4 flex-shrink-0 text-gray-700"
                                            type="checkbox"
                                        >
                                        <img
                                            :alt="user.name"
                                            :src="user.avatar"
                                            class="mr-3 h-8 w-8 flex-shrink-0 rounded-full border border-gray-200 object-cover"
                                        >
                                        <span class="min-w-0 flex-1">
                                            <span class="block truncate text-sm font-medium text-gray-900">{{ user.name }}</span>
                                            <span class="block truncate text-sm text-gray-500">{{ user.email }}</span>
                                        </span>
                                        <span class="ml-4 max-w-[14rem] truncate text-sm text-gray-500">{{ user.role_name }}</span>
                                    </label>
                                    <div v-if="availableUsers.length < 1" class="px-4 py-3 text-sm text-gray-500">
                                        Keine Accounts verfügbar.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="!loading.form && !company.is_operator_company" class="w-full xl:w-1/2 mx-auto bg-white shadow sm:rounded overflow-hidden mt-6">
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Unternehmen löschen</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                Das Unternehmen und alle zugehörigen Daten werden dauerhaft entfernt.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div v-if="!canDeleteCompany">
                                <div class="text-base leading-6 text-gray-800">
                                    {{ $t('Delete protection is active because the following assignments still exist:') }}
                                </div>
                                <div v-if="deleteGuard.blockers.length > 0" class="mt-3 rounded border border-bordergray-100 overflow-hidden">
                                    <div
                                        v-for="blocker in deleteGuard.blockers"
                                        :key="'delete-company-guard-blocker-' + blocker.key"
                                        class="flex items-center justify-between gap-3 px-4 py-3 text-sm text-gray-700 border-t border-bordergray-100 first:border-t-0"
                                    >
                                        <span class="min-w-0">{{ blocker.label }}</span>
                                        <span class="whitespace-nowrap">{{ blocker.count }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                Das Unternehmen und alle zugehörigen Daten werden dauerhaft entfernt.
                            </div>
                        </div>
                    </div>
                    <div class="stage-footer-surface text-right px-6 py-3 border-t border-bordergray-100">
                        <span class="inline-flex">
                            <button
                                :disabled="!canDeleteCompany"
                                :class="canDeleteCompany ? 'btn btn-red rounded shadow-sm' : 'btn btn-gray rounded shadow-sm opacity-60 cursor-not-allowed'"
                                type="button"
                                @click="openDeleteCompanyModal"
                            >
                                Unternehmen löschen
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>

        <confirm-dialog :open="deleteCompanyModal" title="Unternehmen löschen" text="Soll dieses Unternehmen wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden." confirm-label="Unternehmen löschen" confirm-variant="danger" @close="deleteCompanyModal = false" @confirm="deleteCompany" />
    </main>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {resolveReturnToPath} from "@/utils/list-route-state";

export default {
    components: {ConfirmDialog},
    name: 'dashboard-companies-edit',
    metaInfo() {
        return {
            title: 'Unternehmen bearbeiten',
        };
    },
    data() {
        return {
            deleteCompanyModal: false,
            loading: {
                form: true,
                users: false,
            },
            availableUsers: [],
            customerCompanyOptions: [],
            imageRemoved: false,
            originalImage: null,
            company: {
                id: null,
                company_type: 'company',
                name: '',
                website: '',
                email: '',
                phone: '',
                linkedin_url: '',
                xing_url: '',
                instagram_url: '',
                facebook_url: '',
                tax_number: '',
                vat_id: '',
                country: '',
                city: '',
                postal_code: '',
                street: '',
                house_number: '',
                is_customer_company: false,
                is_provider_company: false,
                is_contact_company: false,
                is_operator_company: false,
                relationships_visible_to_customers: false,
                relationships_visible_to_employees: false,
                relationships_visible_to_service_providers: false,
                relationship_customer_company_ids: [],
                contact_user_ids: [],
                customer_hourly_rate: null,
                customer_vat_rate: null,
                provider_hourly_rate: null,
                provider_vat_rate: null,
                delete_guard: {
                    deletable: true,
                    message: '',
                    blockers: [],
                },
                image: null,
                image_preview: null,
            },
        };
    },
    computed: {
        companyListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/workspace/companies');
        },
        companyRoleLabel() {
            if (this.company.is_operator_company) {
                return 'Betreiber';
            }

            if (this.company.is_customer_company) {
                return 'Kunde';
            }

            if (this.company.is_provider_company) {
                return 'Dienstleister';
            }

            if (this.company.is_contact_company) {
                return 'Externer Kontakt';
            }

            return '-';
        },
        hasCustomImage() {
            if (this.company.image instanceof File) {
                return true;
            }

            return !!this.originalImage && !this.imageRemoved;
        },
        deleteGuard() {
            return this.company && this.company.delete_guard ? this.company.delete_guard : {
                deletable: true,
                message: this.$t('The company can be deleted.').toString(),
                blockers: [],
            };
        },
        canDeleteCompany() {
            return !!this.deleteGuard.deletable;
        },
        imagePreviewUrl() {
            if (this.company.image_preview) {
                return this.company.image_preview;
            }

            if (!this.imageRemoved && this.originalImage) {
                return this.originalImage;
            }

            return this.generateFallbackImage();
        },
    },
    mounted() {
        this.getCompany();
        this.loadCustomerCompanyOptions();
    },
    methods: {
        loadCustomerCompanyOptions() {
            axios.get('api/workspace/admin/companies').then((response) => {
                const items = response.data && Array.isArray(response.data.items) ? response.data.items : [];
                this.customerCompanyOptions = items
                    .filter((company) => !!company.is_customer_company)
                    .map((company) => ({
                        id: Number(company.id),
                        name: company.name,
                        image: company.image || '/images/default/icon.png',
                    }));
            }).catch(() => {
                this.customerCompanyOptions = [];
            });
        },
        getCompany() {
            const self = this;
            self.loading.form = true;

            axios.get('api/workspace/admin/companies/' + self.$route.params.id).then(function (response) {
                const data = response.data || {};
                self.company.id = data.id || null;
                self.company.company_type = data.company_type || 'company';
                self.company.name = data.name || '';
                self.company.website = self.normalizeWebsite(data.website || '');
                self.company.email = data.email || '';
                self.company.phone = data.phone || '';
                self.company.linkedin_url = data.linkedin_url || '';
                self.company.xing_url = data.xing_url || '';
                self.company.instagram_url = data.instagram_url || '';
                self.company.facebook_url = data.facebook_url || '';
                self.company.tax_number = data.tax_number || '';
                self.company.vat_id = data.vat_id || '';
                self.company.country = data.country || '';
                self.company.city = data.city || '';
                self.company.postal_code = data.postal_code || '';
                self.company.street = data.street || '';
                self.company.house_number = data.house_number || '';
                self.company.is_customer_company = !!data.is_customer_company;
                self.company.is_provider_company = !!data.is_provider_company;
                self.company.is_contact_company = !!data.is_contact_company;
                self.company.is_operator_company = !!data.is_operator_company;
                self.company.relationships_visible_to_customers = !!data.relationships_visible_to_customers;
                self.company.relationships_visible_to_employees = !!data.relationships_visible_to_employees;
                self.company.relationships_visible_to_service_providers = !!data.relationships_visible_to_service_providers;
                self.company.relationship_customer_company_ids = Array.isArray(data.relationship_customer_company_ids)
                    ? data.relationship_customer_company_ids.map((id) => Number(id))
                    : [];
                self.company.contact_user_ids = Array.isArray(data.contact_user_ids)
                    ? data.contact_user_ids.map((id) => Number(id))
                    : [];
                self.company.customer_hourly_rate = self.normalizeDecimalValue(data.customer_hourly_rate);
                self.company.customer_vat_rate = self.normalizeDecimalValue(data.customer_vat_rate);
                self.company.provider_hourly_rate = self.normalizeDecimalValue(data.provider_hourly_rate);
                self.company.provider_vat_rate = self.normalizeDecimalValue(data.provider_vat_rate);
                self.company.delete_guard = data.delete_guard || {
                    deletable: true,
                    message: self.$i18n.t('The company can be deleted.').toString(),
                    blockers: [],
                };
                self.company.image = null;
                self.company.image_preview = null;
                self.originalImage = data.image || null;
                self.imageRemoved = false;
                self.loading.form = false;
                if (self.company.is_contact_company) {
                    self.getAvailableUsers();
                }
            }).catch(function () {
                self.loading.form = false;
                self.$router.push(self.companyListPath);
            });
        },
        getAvailableUsers() {
            const self = this;
            self.loading.users = true;

            axios.get('api/workspace/admin/companies/' + self.$route.params.id + '/users').then(function (response) {
                self.availableUsers = Array.isArray(response.data.items) ? response.data.items : [];
                if (self.company.is_contact_company) {
                    self.company.contact_user_ids = self.availableUsers
                        .filter((user) => !!user.assigned)
                        .map((user) => Number(user.id));
                }
                self.loading.users = false;
            }).catch(function () {
                self.availableUsers = [];
                self.loading.users = false;
            });
        },
        saveCompany() {
            const self = this;
            self.loading.form = true;

            const formData = new FormData();
            formData.append('_method', 'PUT');
            formData.append('name', self.company.name || '');
            formData.append('company_type', self.company.company_type || 'company');
            formData.append('website', self.normalizeWebsite(self.company.website) || '');
            formData.append('email', self.company.email || '');
            formData.append('phone', self.company.phone || '');
            formData.append('linkedin_url', self.company.linkedin_url || '');
            formData.append('xing_url', self.company.xing_url || '');
            formData.append('instagram_url', self.company.instagram_url || '');
            formData.append('facebook_url', self.company.facebook_url || '');
            formData.append('tax_number', self.company.tax_number || '');
            formData.append('vat_id', self.company.vat_id || '');
            formData.append('country', self.company.country || '');
            formData.append('city', self.company.city || '');
            formData.append('postal_code', self.company.postal_code || '');
            formData.append('street', self.company.street || '');
            formData.append('house_number', self.company.house_number || '');
            formData.append('customer_hourly_rate', self.normalizeDecimalValue(self.company.customer_hourly_rate) || '');
            formData.append('customer_vat_rate', self.normalizeDecimalValue(self.company.customer_vat_rate) || '');
            formData.append('provider_hourly_rate', self.normalizeDecimalValue(self.company.provider_hourly_rate) || '');
            formData.append('provider_vat_rate', self.normalizeDecimalValue(self.company.provider_vat_rate) || '');
            formData.append('relationships_visible_to_customers', self.company.relationships_visible_to_customers ? '1' : '0');
            formData.append('relationships_visible_to_employees', self.company.relationships_visible_to_employees ? '1' : '0');
            formData.append('relationships_visible_to_service_providers', self.company.relationships_visible_to_service_providers ? '1' : '0');
            self.company.relationship_customer_company_ids.forEach(function (companyId, index) {
                formData.append(`relationship_customer_company_ids[${index}]`, String(Number(companyId)));
            });
            if (self.company.is_contact_company) {
                formData.append('sync_contact_user_assignments', '1');
                self.company.contact_user_ids.forEach(function (userId, index) {
                    formData.append(`contact_user_ids[${index}]`, String(Number(userId)));
                });
            }
            if (self.imageRemoved) {
                formData.append('remove_image', '1');
            }
            if (self.company.image instanceof File) {
                formData.append('image', self.company.image);
            }

            axios.post('api/workspace/admin/companies/' + self.$route.params.id, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }).then(function (response) {
                const company = response.data && response.data.company ? response.data.company : null;
                if (company) {
                    self.company.is_customer_company = !!company.is_customer_company;
                    self.company.is_provider_company = !!company.is_provider_company;
                    self.company.is_contact_company = !!company.is_contact_company;
                    self.company.is_operator_company = !!company.is_operator_company;
                    self.company.relationships_visible_to_customers = !!company.relationships_visible_to_customers;
                    self.company.relationships_visible_to_employees = !!company.relationships_visible_to_employees;
                    self.company.relationships_visible_to_service_providers = !!company.relationships_visible_to_service_providers;
                    self.company.contact_user_ids = Array.isArray(company.contact_user_ids)
                        ? company.contact_user_ids.map((id) => Number(id))
                        : self.company.contact_user_ids;
                    self.company.customer_hourly_rate = self.normalizeDecimalValue(company.customer_hourly_rate);
                    self.company.customer_vat_rate = self.normalizeDecimalValue(company.customer_vat_rate);
                    self.company.provider_hourly_rate = self.normalizeDecimalValue(company.provider_hourly_rate);
                    self.company.provider_vat_rate = self.normalizeDecimalValue(company.provider_vat_rate);
                    self.company.delete_guard = company.delete_guard || self.company.delete_guard;
                }
                if (self.company.is_contact_company) {
                    self.getAvailableUsers();
                }
                self.loading.form = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success',
                });
            }).catch(function () {
                self.loading.form = false;
            });
        },
        deleteCompany() {
            const self = this;
            if (!self.canDeleteCompany) {
                return;
            }
            self.loading.form = true;
            axios.delete('api/workspace/admin/companies/' + self.$route.params.id).then(function () {
                self.loading.form = false;
                self.deleteCompanyModal = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success',
                });
                self.$router.push(self.companyListPath);
            }).catch(function (error) {
                self.loading.form = false;
                self.deleteCompanyModal = false;
                if (error && error.response && error.response.status === 422 && error.response.data && error.response.data.delete_guard) {
                    self.company.delete_guard = error.response.data.delete_guard;
                }
            });
        },
        openDeleteCompanyModal() {
            if (!this.canDeleteCompany) {
                return;
            }

            this.deleteCompanyModal = true;
        },
        selectImage() {
            this.$refs.changeImage.click();
        },
        removeImage() {
            this.imageRemoved = true;
            this.company.image = null;
            this.company.image_preview = null;
            if (this.$refs.changeImage) {
                this.$refs.changeImage.value = '';
            }
        },
        changeImage(event) {
            const self = this;
            if (!event.target.files.length) {
                return;
            }

            const file = event.target.files[0];
            if (['image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg'].includes(file.type)) {
                self.imageRemoved = false;
                self.company.image = file;
                self.company.image_preview = URL.createObjectURL(file);
                return;
            }

            self.$notify({
                title: self.$i18n.t('Unsupported file type').toString(),
                text: self.$i18n.t('Only image type files are accepted').toString(),
                type: 'error',
            });
            event.target.value = '';
        },
        normalizeWebsite(value) {
            if (!value) {
                return null;
            }

            return String(value).replace(/^https?:\/\//i, '').trim();
        },
        normalizeDecimalValue(value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return null;
            }

            const normalized = String(value).replace(',', '.').trim();
            if (normalized === '') {
                return null;
            }

            const parsed = Number(normalized);
            if (!Number.isFinite(parsed)) {
                return null;
            }

            return parsed.toFixed(2);
        },
        generateFallbackImage() {
            const seedText = String(this.company.name || 'C').trim() || 'C';
            const firstChar = seedText.charAt(0).toUpperCase();
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="80" height="80"><defs><linearGradient id="companyGradientEditDashboard" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="hsl(210 35% 48%)"/><stop offset="100%" stop-color="hsl(210 35% 34%)"/></linearGradient></defs><rect width="80" height="80" fill="url(#companyGradientEditDashboard)"/><text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle" fill="rgba(255,255,255,0.55)" font-family="Arial, sans-serif" font-size="34" font-weight="700">${firstChar}</text></svg>`;
            return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
        },
    },
};
</script>

<style lang="scss">
@reference "../../../../../sass/tailwind-reference.css";

.avatar-editor {
    .avatar-editor-remove {
        @apply hidden;
    }

    &:hover {
        .avatar-editor-remove {
            @apply inline-flex;
        }
    }
}
</style>
